import { IntegrationProvider } from "@repo/app-types/entities/integration";

// Integration metadata, in its own module so both the settings section and the
// breadcrumb label can read it without importing the section component.

/** The overview's groups, in the order they are shown. */
export const integrationCategoryConfigs = [
  {
    category: "recordingSources",
    title: "Call recording sources",
    description:
      "Import call recordings for transcription, coaching, and performance analysis.",
  },
  {
    category: "notifications",
    title: "Notifications",
    description:
      "Send automation results to channels or directly to teammates.",
  },
  {
    category: "knowledgeBases",
    title: "Knowledge base sync",
    description:
      "Sync external knowledge bases into read-only Alpharun knowledge bases.",
  },
] as const;

/**
 * What an integration is for, which decides the overview group it is listed
 * in. Derived from the list so every category has a group to render in.
 */
export type IntegrationCategory =
  (typeof integrationCategoryConfigs)[number]["category"];

export type IntegrationConfig = {
  provider: IntegrationProvider;
  slug: string;
  name: string;
  description: string;
  disconnectWarning: string;
  category: IntegrationCategory;
  /**
   * Set up and changed only by Alpharun (a script, or for the API upload
   * integration the first upload): shown only when the boot
   * payload carries the company's row, with a read-only detail page
   * (`alpharunOperatedIntegrationDetailSection.tsx`) and no
   * connect/disconnect.
   */
  isAlpharunOperated?: true;
};

/** The slug the (single) "Recording S3 intake" config uses for either intake provider. */
export const RECORDING_S3_INTAKE_SLUG = "recording-s3-intake";

// Registry of the integrations rendered in the overview table and detail pages.
// Add an entry to surface a new integration.
export const integrationConfigs: IntegrationConfig[] = [
  {
    provider: IntegrationProvider.SLACK,
    slug: "slack",
    name: "Slack",
    description:
      "Connect Slack to send automation results to channels or directly to teammates.",
    disconnectWarning:
      "Disconnecting Slack will stop automation results from being delivered to Slack.",
    category: "notifications",
  },
  {
    provider: IntegrationProvider.TEAMS,
    slug: "microsoft-teams",
    name: "Microsoft Teams",
    description:
      "Connect Microsoft Teams to send automation results to channels or directly to teammates.",
    disconnectWarning:
      "Disconnecting Microsoft Teams will stop automation results from being delivered to Teams.",
    category: "notifications",
  },
  {
    provider: IntegrationProvider.GURU,
    slug: "guru",
    name: "Guru",
    description:
      "Sync Guru collections into Alpharun knowledge bases so agents answer from the same articles your team does. Each collection becomes its own read-only knowledge base, kept up to date in the background.",
    disconnectWarning:
      "Disconnecting Guru will delete its synced knowledge bases and documents.",
    category: "knowledgeBases",
  },
  {
    provider: IntegrationProvider.KUSTOMER,
    slug: "kustomer",
    name: "Kustomer",
    description:
      "Connect Kustomer so its knowledge bases are synced into read-only Alpharun knowledge bases.",
    disconnectWarning:
      "Disconnecting Kustomer will delete its synced knowledge bases and documents.",
    category: "knowledgeBases",
  },
  {
    provider: IntegrationProvider.GENESYS,
    slug: "genesys",
    name: "Genesys Cloud",
    description:
      "Import Genesys Cloud call recordings into Alpharun for transcription, coaching, and performance analysis.",
    disconnectWarning:
      "Disconnecting Genesys Cloud will stop new call recordings from syncing.",
    category: "recordingSources",
  },
  {
    provider: IntegrationProvider.ZOOM_CONTACT_CENTER,
    slug: "zoom-contact-center",
    name: "Zoom Contact Center",
    description:
      "Import Zoom Contact Center call recordings into Alpharun for transcription, coaching, and performance analysis.",
    disconnectWarning:
      "Disconnecting Zoom Contact Center will stop new call recordings from syncing.",
    category: "recordingSources",
  },
  {
    // One entry for both intake providers (`five9_recording_s3_intake` and
    // `ringcentral_recording_s3_intake`): a company has at most one, created
    // from HQ, and the overview matches on the slug rather than this provider
    // key. Shown only when the boot payload carries such a row. There is no
    // connect/disconnect — the overview special-cases it — so
    // `disconnectWarning` is never surfaced.
    provider: IntegrationProvider.FIVE9_RECORDING_S3_INTAKE,
    slug: RECORDING_S3_INTAKE_SLUG,
    name: "Recording S3 intake",
    description:
      "Call recordings delivered to your Alpharun S3 bucket are imported for transcription, coaching, and performance analysis.",
    disconnectWarning: "",
    category: "recordingSources",
  },
  {
    // scripts/setupNiceCxoneIntegration.ts
    provider: IntegrationProvider.NICE_CXONE,
    slug: "nice-cxone",
    name: "NICE CXone",
    description:
      "Call recordings from your NICE CXone account are imported for transcription, coaching, and performance analysis.",
    disconnectWarning: "",
    category: "recordingSources",
    isAlpharunOperated: true,
  },
  {
    // scripts/setupRingCentralRingExIntegration.ts
    provider: IntegrationProvider.RINGCENTRAL_RINGEX,
    slug: "ringcentral",
    name: "RingCentral",
    description:
      "Call recordings from your RingCentral phone system are imported for transcription, coaching, and performance analysis.",
    disconnectWarning: "",
    category: "recordingSources",
    isAlpharunOperated: true,
  },
  {
    // Created by the company's first `POST /recording-imports` upload
    // (`findOrCreateApiUploadIntegrationDb`), never paused.
    provider: IntegrationProvider.API_RECORDING_UPLOAD,
    slug: "recording-import-api",
    name: "Recording import API",
    description:
      "Call recordings sent to the recording import endpoints of the Alpharun API (POST /recording-imports and /recording-imports/batch) are imported for transcription, coaching, and performance analysis.",
    disconnectWarning: "",
    category: "recordingSources",
    isAlpharunOperated: true,
  },
];

/** Resolves an integration's display name from its slug, or null if unknown. */
export function getIntegrationNameBySlug(
  slug: string | undefined,
): string | null {
  return (
    integrationConfigs.find((config) => config.slug === slug)?.name ?? null
  );
}

/** The configs whose detail pages are the shared Alpharun-operated one. */
export const alpharunOperatedIntegrationConfigs = integrationConfigs.filter(
  (config) => config.isAlpharunOperated,
);
